export enum StageTitle {
  none = 'No action planned',
  running = 'GatorAI is working',
  queued = 'Waiting to be processed',
  done = 'Things are great',
  needTouch = 'Human touch needed',
}

export enum StageColor {
  running = '#629CBA',
  runningText = '#256D91',
  success = '#10B981',
  successText = '#065F46',
  pending = '#92400E',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  pendingText = '#92400E',
  danger = '#EF4444',
  dangerText = '#991B1B',
  done = '#737373',
  doneText = '#262626',
}

export enum ContactStatus {
  Active = 0,
  Hidden = 1,
  Deleted = 2,
}

export enum ContactStage {
  ENRICHMENT_QUEUED = 'ENRICHMENT_QUEUED',
  ENRICHING = 'ENRICHING',
  ENRICHMENT_FAILED = 'ENRICHMENT_FAILED',
  ENRICHED = 'ENRICHED',
  PREPARING = 'PREPARING',
  PREPARE_FAILED = 'PREPARE_FAILED',
  GENERATION_QUEUED = 'GENERATION_QUEUED',
  GENERATING = 'GENERATING',
  GENERATION_APPROVAL_PENDING = 'GENERATION_APPROVAL_PENDING',
  GENERATION_FAILED = 'GENERATION_FAILED',
  GENERATED = 'GENERATED',
  SCHEDULE_QUEUED = 'SCHEDULE_QUEUED',
  SCHEDULING = 'SCHEDULING',
  SCHEDULE_FAILED = 'SCHEDULE_FAILED',
  SCHEDULED = 'SCHEDULED',
  SENDING = 'SENDING',
  SEND_FAILED = 'SEND_FAILED',
  SENT = 'SENT',
  ABORTED = 'ABORTED',
  EXITED = 'EXITED',
  COMPLETED = 'COMPLETED',
  IDLE = 'IDLE',
}

export interface IContactStageInfo {
  labelColor: string;
  textColor: string;
  isLoading: boolean; // 是否展示加载中样式 （渐变色）
  displayText: string;
  title: string;
  description?: string;
}

export const ContactStageInfoMap: Record<ContactStage, IContactStageInfo> = {
  [ContactStage.ENRICHMENT_QUEUED]: {
    labelColor: StageColor.pending,
    textColor: StageColor.pendingText,
    isLoading: false,
    displayText: 'Enrichment Queued',
    title: StageTitle.queued,
    description: 'Waiting for GatorAI to enrich the contact for better personalized outreach.',
  },
  [ContactStage.ENRICHING]: {
    labelColor: StageColor.running,
    textColor: StageColor.runningText,
    isLoading: true,
    displayText: 'Enriching',
    title: StageTitle.running,
    description: 'GatorAI is enriching the contact.',
  },
  [ContactStage.ENRICHMENT_FAILED]: {
    labelColor: StageColor.danger,
    textColor: StageColor.dangerText,
    isLoading: false,
    displayText: 'Enrichment Failed',
    title: StageTitle.needTouch,
    description: 'Contact enrichment failed. Please address the task.',
  },
  /**
   * ENRICHED  不在UI层展示  是一个过渡状态
   * 处于 ENRICHED 状态时 前端直接展示outreach状态
   */
  [ContactStage.ENRICHED]: {
    labelColor: StageColor.success,
    textColor: StageColor.successText,
    isLoading: false,
    displayText: 'Enriched',
    title: StageTitle.done,
    description: 'GatorAI has successfully enriched the contact.',
  },
  [ContactStage.PREPARING]: {
    labelColor: StageColor.running,
    textColor: StageColor.runningText,
    isLoading: true,
    displayText: 'Preparing',
    title: StageTitle.running,
    description: 'GatorAI is preparing the outreach process.',
  },
  [ContactStage.PREPARE_FAILED]: {
    labelColor: StageColor.danger,
    textColor: StageColor.dangerText,
    isLoading: false,
    displayText: 'Prepare Failed',
    title: StageTitle.needTouch,
    description: 'GatorAI failed to start the outreach process. Please address the task.',
  },
  [ContactStage.GENERATION_QUEUED]: {
    labelColor: StageColor.pending,
    textColor: StageColor.pendingText,
    isLoading: false,
    displayText: 'Generation Queued',
    title: StageTitle.queued,
    description: 'Waiting for GatorAI to generate personalized email.',
  },
  [ContactStage.GENERATING]: {
    labelColor: StageColor.running,
    textColor: StageColor.runningText,
    isLoading: true,
    displayText: 'Generating',
    title: StageTitle.running,
    description: 'GatorAI is generating personalized outreach email.',
  },
  [ContactStage.GENERATION_APPROVAL_PENDING]: {
    labelColor: StageColor.danger,
    textColor: StageColor.dangerText,
    isLoading: false,
    displayText: 'Generation Approval Pending',
    title: StageTitle.needTouch,
    description: 'Outreach email generated and awaits for approval!',
  },
  [ContactStage.GENERATION_FAILED]: {
    labelColor: StageColor.danger,
    textColor: StageColor.dangerText,
    isLoading: false,
    displayText: 'Generation Failed',
    title: StageTitle.needTouch,
    description: 'Personalized outreach email generation failed. Please address the task.',
  },
  [ContactStage.GENERATED]: {
    labelColor: StageColor.success,
    textColor: StageColor.successText,
    isLoading: false,
    displayText: 'Generated',
    title: StageTitle.done,
    description: 'Outreach email generated.',
  },
  [ContactStage.SCHEDULE_QUEUED]: {
    labelColor: StageColor.pending,
    textColor: StageColor.pendingText,
    isLoading: false,
    displayText: 'Schedule Queued',
    title: StageTitle.queued,
    description: 'Waiting for GatorAI to finalize the outreach schedule.',
  },
  [ContactStage.SCHEDULING]: {
    labelColor: StageColor.running,
    textColor: StageColor.runningText,
    isLoading: true,
    displayText: 'Scheduling',
    title: StageTitle.running,
    description: 'Scheduling personalized outreach email send time',
  },
  [ContactStage.SCHEDULE_FAILED]: {
    labelColor: StageColor.danger,
    textColor: StageColor.dangerText,
    isLoading: false,
    displayText: 'Schedule Failed',
    title: StageTitle.needTouch,
    description: 'GatorAI failed to schedule the outreach email. Please address the task.',
  },
  [ContactStage.SCHEDULED]: {
    labelColor: StageColor.success,
    textColor: StageColor.successText,
    isLoading: false,
    displayText: 'Scheduled',
    title: StageTitle.done,
    description: 'Outreach email has been scheduled.',
  },
  [ContactStage.SENDING]: {
    labelColor: StageColor.running,
    textColor: StageColor.runningText,
    isLoading: true,
    displayText: 'Sending',
    title: StageTitle.running,
    description: 'GatorAI is sending personalized outreach email.',
  },
  [ContactStage.SEND_FAILED]: {
    labelColor: StageColor.danger,
    textColor: StageColor.dangerText,
    isLoading: false,
    displayText: 'Send Failed',
    title: StageTitle.needTouch,
    description: 'Personalized outreach email sending failed. Please address the task.',
  },
  [ContactStage.SENT]: {
    labelColor: StageColor.success,
    textColor: StageColor.successText,
    isLoading: false,
    displayText: 'Sent',
    title: StageTitle.done,
    description: 'Personalized outreach email sent.',
  },
  [ContactStage.ABORTED]: {
    labelColor: StageColor.done,
    textColor: StageColor.doneText,
    isLoading: false,
    displayText: 'Aborted',
    title: StageTitle.none,
    description: 'Outreach effort aborted.',
  },
  [ContactStage.EXITED]: {
    labelColor: StageColor.done,
    textColor: StageColor.doneText,
    isLoading: false,
    displayText: 'Exited',
    title: StageTitle.none,
    description: 'Outreach plan executed.',
  },
  [ContactStage.COMPLETED]: {
    labelColor: StageColor.done,
    textColor: StageColor.doneText,
    isLoading: false,
    displayText: 'Completed',
    title: StageTitle.done,
    description: 'Personalized outreach plan execution completed.',
  },
  [ContactStage.IDLE]: {
    labelColor: StageColor.done,
    textColor: StageColor.doneText,
    isLoading: false,
    displayText: 'Idle',
    title: StageTitle.none,
    description: 'Manual only plan activated.',
  },
};
